import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { API_ERROR_CODES } from '@mcikm-vote/shared';

import { useAdminSitzung, useAnmelden } from '../../admin/useAdminSitzung';
import { Markenkopf } from '../../components/Markenkopf';
import { ThemeToggle } from '../../components/ThemeToggle';

/**
 * Anmeldung zum Administrationsbereich (§5.1, §5.2).
 *
 * Zeigt bei `SITZUNG_BESTEHT` einen Bestätigungsdialog: die Übernahme geschieht nie
 * unbeabsichtigt, und die bisherige Sitzung wird dabei sofort beendet (§5.2).
 */
export function AnmeldungSeite(): ReactElement {
  const [suchparameter] = useSearchParams();
  const sitzung = useAdminSitzung();
  const anmelden = useAnmelden();

  const [benutzername, setBenutzername] = useState('');
  const [passwort, setPasswort] = useState('');
  const [uebernahmeAngefragt, setUebernahmeAngefragt] = useState(false);

  // Bereits angemeldet: direkt in den geschützten Bereich.
  if (sitzung.isSuccess) {
    return <Navigate to="/verwaltung/uebersicht" replace />;
  }

  const abgelaufen = suchparameter.get('grund') === 'abgelaufen';
  const fehler = anmelden.error;
  const sitzungBesteht = fehler?.code === API_ERROR_CODES.SITZUNG_BESTEHT;

  function absenden(ereignis: FormEvent<HTMLFormElement>, uebernehmen: boolean): void {
    ereignis.preventDefault();
    setUebernahmeAngefragt(uebernehmen);
    anmelden.mutate({ benutzername, passwort, ...(uebernehmen ? { uebernehmen: true } : {}) });
  }

  return (
    <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-6 p-6">
      <Markenkopf infotext="Verwaltung" />

      {abgelaufen && !anmelden.isSuccess && (
        <p
          role="status"
          className="border-warnung/40 bg-flaeche-gedaempft text-text rounded-lg border p-3 text-sm"
        >
          Die Sitzung ist wegen Untätigkeit abgelaufen. Bitte melden Sie sich erneut an.
        </p>
      )}

      <form
        onSubmit={(ereignis) => {
          absenden(ereignis, false);
        }}
        className="border-rand bg-flaeche flex flex-col gap-4 rounded-xl border p-4 shadow-sm"
      >
        <div className="flex flex-col gap-1">
          <label htmlFor="benutzername" className="text-sm font-medium">
            Benutzername
          </label>
          <input
            id="benutzername"
            name="benutzername"
            type="text"
            autoComplete="username"
            required
            value={benutzername}
            onChange={(ereignis) => {
              setBenutzername(ereignis.target.value);
            }}
            className="border-rand bg-hintergrund min-h-touchflaeche rounded-md border px-3"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="passwort" className="text-sm font-medium">
            Passwort
          </label>
          <input
            id="passwort"
            name="passwort"
            type="password"
            autoComplete="current-password"
            required
            value={passwort}
            onChange={(ereignis) => {
              setPasswort(ereignis.target.value);
            }}
            className="border-rand bg-hintergrund min-h-touchflaeche rounded-md border px-3"
          />
        </div>

        {fehler !== null && !sitzungBesteht && (
          <p role="alert" className="text-fehler text-sm font-medium">
            {fehler.message}
          </p>
        )}

        <button
          type="submit"
          disabled={anmelden.isPending && !uebernahmeAngefragt}
          className="bg-akzent text-akzent-text transition hover:brightness-110 min-h-touchflaeche rounded-md font-medium disabled:pointer-events-none disabled:opacity-60"
        >
          {anmelden.isPending && !uebernahmeAngefragt ? 'Anmelden …' : 'Anmelden'}
        </button>
      </form>

      {sitzungBesteht && (
        <form
          onSubmit={(ereignis) => {
            absenden(ereignis, true);
          }}
          role="alertdialog"
          aria-labelledby="uebernahme-titel"
          className="border-warnung/50 bg-flaeche flex flex-col gap-3 rounded-xl border p-4 shadow-sm"
        >
          <h2 id="uebernahme-titel" className="text-base font-semibold">
            Es besteht bereits eine Sitzung
          </h2>
          <p className="text-text-gedaempft text-sm">
            Der Administrationsbereich ist derzeit auf einem anderen Gerät geöffnet. Bei der
            Übernahme wird die bisherige Sitzung sofort beendet. Bereits gespeicherte Daten und
            laufende Abstimmungen bleiben unverändert.
          </p>
          <button
            type="submit"
            disabled={anmelden.isPending}
            className="bg-akzent text-akzent-text transition hover:brightness-110 min-h-touchflaeche rounded-md font-medium disabled:pointer-events-none disabled:opacity-60"
          >
            {anmelden.isPending ? 'Sitzung wird übernommen …' : 'Sitzung übernehmen'}
          </button>
        </form>
      )}

      <ThemeToggle />
    </main>
  );
}
